import * as React from 'react';
import { NonRouterModal } from './Modal';
import { Autocomplete, Button, TextField, Typography } from '@mui/material';
import { AlertProps } from '@mui/material/Alert';
import { Product, getProducts } from '../types/Product';
import { httpGet, httpPost } from '../Web';
import { Workshop, getWorkshops } from '../types/Workshop';
import { httpGetIds } from '../Utils';
import { WorkshopProduct } from '../types/WorkshopProducts';
import { WorkshopProductImport, importWorkshopProductSettings } from '../types/WorkshopTestCases';
import { UserContext } from '../UserProvider';
import { Company } from '../types/Company';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { FeatureNotAvailableIcon } from '../subs/FeatureNotAvailableIcon';
import { useSnackbar } from '../../contexts/SnackBarContext';

const getWorkshopProducts = async (abortController: AbortController): Promise<WorkshopProduct[]> => {
    try {
        let workshops = await getWorkshops(abortController);
        let workshopIds = workshops.result.map((ws: Workshop) => ws.id);

        let workshopProductRequests = await httpGetIds('/api/workshopproduct', workshopIds);

        console.log('pr: ', workshopProductRequests);
        return workshopProductRequests.flatMap(wpr => wpr.result);
    } catch (error) {
        console.error('getWorkshopProducts: ', error);
        return Promise.reject();
    }
}

export const ImportWorkshopProductNRModal = ({ workshopId, currentWorkshopProductId, givenProductID, refreshWorkshopProductHandler, openState, setOpenState }:
    {
        workshopId: number, currentWorkshopProductId: number, givenProductID: number,
        refreshWorkshopProductHandler: (workshopProduct: WorkshopProduct) => void,
        openState: boolean, setOpenState: React.Dispatch<React.SetStateAction<boolean>>
    }) => {
    const { showSnackbar } = useSnackbar();

    const userContext = React.useContext(UserContext);

    const [userCompanyId, setuserCompanyId] = React.useState<Number>();
    const [companyProducts, setCompanyProducts] = React.useState<Product[]>([]);
    const [sourceProductId, setSourceProductId] = React.useState<Number>();
    const [sourceProduct, setSourceProduct] = React.useState<Product | null>();
    const [productSelectDisabled, setproductSelectDisabled] = React.useState<boolean>(true);

    const [workshopProducts, setWorkshopProducts] = React.useState<WorkshopProduct[]>([]);
    const [currentWorkshopProduct, setcurrentWorkshopProduct] = React.useState<WorkshopProduct>();
    const [workshops, setWorkshops] = React.useState<Workshop[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    console.log('WorkshopId: ', workshopId);

    React.useEffect(() => {
        getWorkshops(new AbortController()).then(wq => setWorkshops(wq.result));
        console.log('Workshops: loaded');
    }, []);

    React.useEffect(() => {
        async function getCompanies() {
            let companies = await httpGet('/api/company', new AbortController());

            if (companies.failed) {
                //  setError(productsResponse.errorText)
                return;
            }

            const userCompanyIdLoc = companies.result.find((company: Company) => userContext.user.companyName === company.name).id;

            setuserCompanyId(userCompanyIdLoc);
        }

        getCompanies();
    }, [userContext.user.companyName]);

    React.useEffect(() => {
        async function getCompanyProducts() {
            const productsResponse = await getProducts(new AbortController());

            if (productsResponse.failed) {
                showUserFeedback(productsResponse.errorText)
                return;
            }

            const cmpnyProds = productsResponse.result.filter(product => product.companyId === userCompanyId);
            setCompanyProducts(cmpnyProds);
        }

        getCompanyProducts();
    }, [userCompanyId]);

    React.useEffect(() => {
        const givenID = Number(givenProductID ?? 0);
        setproductSelectDisabled(givenID ? true : false);
        setSourceProductId(givenID);
    }, [givenProductID]);

    React.useEffect(() => {
        if (sourceProductId === undefined || sourceProductId === 0) {
            setSourceProduct(null);
        }
        else httpGet(`/api/product/get/${sourceProductId}`, new AbortController()).then(query => setSourceProduct(query.result));
    }, [sourceProductId]);

    React.useEffect(() => {
        getWorkshopProducts(new AbortController()).then(wps => {
            setcurrentWorkshopProduct(wps.filter(wp => (wp.id === Number(currentWorkshopProductId)))[0]);
            setWorkshopProducts(wps.filter(wp => (wp.productId === sourceProduct?.id && wp.id !== Number(currentWorkshopProductId))));
        });
    }, [currentWorkshopProductId, sourceProduct]);

    const importWPFromPreviousWorkshopHandler = async (currentInstance: WorkshopProduct, previousInstance: WorkshopProduct) => {
        const workshopProductImport: WorkshopProductImport = {
            workshopProductIdFrom: previousInstance.id,
            workshopProductIdTo: currentInstance.id
        };

        const importSettingsResponse = await importWorkshopProductSettings(workshopProductImport, new AbortController());

        if (importSettingsResponse.failed) {
            showUserFeedback(importSettingsResponse.errorText);
            return;
        }
        const importSettingsResult = importSettingsResponse.result;

        if (workshopProductImport.workshopProductIdFrom !== importSettingsResult.workshopProductIdFrom ||
            workshopProductImport.workshopProductIdTo !== importSettingsResult.workshopProductIdTo) {
            showUserFeedback('Import did not complete successfully.');
            return;
        }

        showUserFeedback('Import completed successfully.', 'success');
        refreshWorkshopProductHandler(currentInstance);
        setOpenState(false);
    }

    const columns: GridColDef[] = [{
        field: 'workshop',
        headerName: 'Workshop',
        valueGetter: (value, row) => {
            return workshops.find(ws => ws.id === row.workshopId)?.name;
        },
        flex: 4,
    },
    {
        field: 'version',
        headerName: 'Version',
        valueGetter: (value, row) => {
            return row.productVersion;
        },
        flex: 4
    }
   /* {
        field: 'isServer',
        headerName: 'Server?',
        valueGetter: (value, row) => {
            return row.isServer;
        },
        renderCell: (params) => params.row.isServer ? <CheckBoxIcon /> : <FeatureNotAvailableIcon />,
        flex: 1,
    },
    {
        field: 'isClient',
        headerName: 'Client?',
        valueGetter: (value, row) => {
            return row.isClient;
        },
        renderCell: (params) => params.row.isClient ? <CheckBoxIcon /> : <FeatureNotAvailableIcon />,
        flex: 1
    }*/
    ];

    const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);

    return <NonRouterModal title={`Previous instances of ${sourceProduct?.name!!}`} openState={openState} setOpenState={setOpenState}
        description={[
            <Typography variant='body2'>{'Import instances from previous workshops intothe current workshop'}</Typography>,
            <Typography variant='bold'>{'Warning: This import will overwrite all currently specified product data!'}</Typography>]} 
        cancel
        buttons={[<Button disabled={selectionModel.length === 0} startIcon={<ImportExportIcon />}
            onClick={() => {
                importWPFromPreviousWorkshopHandler(currentWorkshopProduct!!, workshopProducts.find(wp => wp.id === selectionModel[0])!!);
            }/*error handling?*/}> { 'Import'} </Button >]}>

        <Autocomplete id='ProductName' options={companyProducts ?? []} value={sourceProduct} hidden={productSelectDisabled}
            renderInput={(params) => <TextField multiline {...params} label={"Product name"} />}
            renderOption={(props, product) => {
                return (
                    <li {...props} key={product.id}>
                        {product.name}
                    </li>
                );
            }}
            getOptionLabel={product => product.name}
            onChange={(event, value) => { setSourceProduct(value) }} />

        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <DataGrid
                sx={{
                    "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                        display: "none"
                    },
                }}

                slots={{
                    // **** REMOVED TO GET IT TO COMPILE **** // baseCheckbox: Radio
                }}

                columns={columns} rows={workshopProducts} rowSelectionModel={selectionModel}
                checkboxSelection disableColumnMenu hideFooter showCellVerticalBorder

                onRowSelectionModelChange={(newRowSelectionModel) => {
                    if (newRowSelectionModel.length === 0)
                        setSelectionModel(newRowSelectionModel);
                    else {
                        const selectionSet = new Set(selectionModel);
                        const result = newRowSelectionModel.filter((s) => !selectionSet.has(s));

                        setSelectionModel(result);
                        console.log(selectionModel);
                    }
                }}

                getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'}
            />
        </div>
    </NonRouterModal>
}